import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from "react-redux";
import api from 'state/axios';
import { addDays, compareAsc, isWithinInterval } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import toast from 'react-hot-toast';
import { useTheme, 
  Box, 
  Typography,
  CircularProgress, 
  ToggleButtonGroup,
  ToggleButton, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  ViewModule,
  ViewQuilt,
  SwapVert,
  North,
  South,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { parseUTCDate } from 'state/helpers'
import ScrollToTop from 'components/ScrollToTop';
import StyledButton from 'components/StyledButton';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import ShuttleList from './ShuttleList';
import ShuttleTable from './ShuttleTable';

const CYCLE_MODES = [
  { value: 'all',      label: 'All',      icon: <SwapVert />, sx: {} },
  { value: 'pickup',   label: 'Pickup',   icon: <North />,    sx: { bgcolor: 'primary.light',   color: 'text.primary' } },
  { value: 'takeback', label: 'Takeback', icon: <South />,    sx: { bgcolor: 'purple.light', color: 'text.primary' } },
];


const CrewShuttles = () => {
  useAuthCheck();
  const timeMode = useSelector((state) => state.timemode);
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const { startDate, endDate } = useDateRangeContext();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);
  const [port, setPort] = useState('');
  const [view, setView] = useState('list');
  const [shuttleMode, setShuttleMode] = useState('all');
  const [crewShuttles, setCrewShuttles] = useState([]);
    
  
  const handleCrewShuttles = useCallback(async () => {
    if (!startDate || !endDate) return;

    const today = parseUTCDate(new Date());
    const yesterday = addDays(today, -1);
    const tomorrow = addDays(today, 1);
    
    const boundStart = compareAsc(startDate, yesterday) < 0 ? yesterday : startDate;
    const boundEnd = compareAsc(endDate, tomorrow) > 0 ? tomorrow : endDate;

    if (compareAsc(boundStart, boundEnd) > 0) {
      setCrewShuttles([]);
      return;
    }

    const url = '/crewshuttles';
    const payload = { 
      startDate: addDays(boundStart, -1), 
      endDate: addDays(boundEnd, 1) 
    };
    const res = await api.post(url, payload);

    const filtered = (res.data || []).map(route => {
      const filteredDetails = route.routeDetails.filter(flight => {
        const depDateToCompare =
          timeMode === "UTC"
            ? flight.flightActualDayDep
            : flight.flightActualDayDepLocalBase;
        
        const arrDateToCompare =
          timeMode === "UTC"
            ? flight.flightActualDayArr
            : flight.flightActualDayArrLocalBase;
  
        return (
          isWithinInterval(depDateToCompare, { start: startDate, end: endDate }) ||
          isWithinInterval(arrDateToCompare, { start: startDate, end: endDate })
        );
      });

      return {
        ...route,
        routeDetails: filteredDetails,
      };
    })
    .filter(route => route.routeDetails.length > 0)
    .sort((a, b) => {
      const aDate = timeMode === "UTC"
        ? a.routeDetails[0]?.flightActualDayDep
        : a.routeDetails[0]?.flightActualDayDepLocalBase;

      const bDate = timeMode === "UTC"
        ? b.routeDetails[0]?.flightActualDayDep
        : b.routeDetails[0]?.flightActualDayDepLocalBase;

      return new Date(aDate) - new Date(bDate);
    });

    setCrewShuttles(filtered);
  }, [timeMode, startDate, endDate]);
  

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);
        
        await handleCrewShuttles();         
      } catch (error) {
        setError(error);
        toast.error(error.response?.data?.message || "Failed to fetch data!");
      } finally {
        setLoading(false);
      }
    };

    fetchData();      
  }, [handleCrewShuttles]);
  
  const ports = useMemo(() => {
    if (!Array.isArray(crewShuttles)) return [];

    const portSet = new Set();

    crewShuttles.forEach(route => {
      route.routeDetails?.forEach(f => {
        if (f.dep && f.fullPickup) portSet.add(f.dep);
        if (f.arr && f.fullTakeBack) portSet.add(f.arr);
      });
    });

    const location = user.location;
    const sorted = Array.from(portSet).sort();
    
    if (location && sorted.includes(location)) {
      setPort(location);
    }
    return sorted;
  }, [crewShuttles, user.location]);

  const filteredCrewShuttles = useMemo(() => {
    let result = crewShuttles;

    if (port) {
      result = result
        .map(route => ({
          ...route,
          routeDetails: route.routeDetails.filter(f =>
            (f.fullPickup && f.dep === port) ||
            (f.fullTakeBack && f.arr === port)
          ),
        }))
        .filter(route => route.routeDetails.length > 0);
    }

    if (shuttleMode !== 'all') {
      result = result
        .map(route => ({
          ...route,
          routeDetails: route.routeDetails.filter(f =>
            shuttleMode === 'pickup' ? Boolean(f.fullPickup) : Boolean(f.fullTakeBack)
          ),
        }))
        .filter(route => route.routeDetails.length > 0);
    }

    return result;
  }, [crewShuttles, port, shuttleMode]);


  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      sortable: false,
      filterable: false,      
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: 'routeDetails',
      headerName: 'Legs',
      sortable: false,
      disableColumnMenu: true,
      flex: 12,
      renderCell: (params) => {      
        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',              
            }}
          >
            {params.row?.routeDetails.map((flight, index) => {        
              const fullAtd = timeMode === 'UTC' 
                ? formatInTimeZone(flight.fullAtd || flight.fullEtd, "UTC", "ddMMMyy") 
                : formatInTimeZone(flight.fullAtdLocalBase || flight.fullEtdLocalBase, "UTC", "ddMMMyy")
              const atd = timeMode === 'UTC' ? (flight?.atd || flight?.etd) : (flight?.atdLocalBase || flight?.etdLocalBase);
              const ata = timeMode === 'UTC' ? (flight?.ata || flight?.eta) : (flight?.ataLocalBase || flight?.etaLocalBase);
              const pickup = timeMode === 'UTC' 
                ? flight.fullPickup && formatInTimeZone(flight.fullPickup, "UTC", "HH:mm") 
                : flight.fullPickupLocalBase && formatInTimeZone(flight.fullPickupLocalBase, "UTC", "HH:mm");
              const takeBack = timeMode === 'UTC' 
                ? flight.fullTakeBack && formatInTimeZone(flight.fullTakeBack, "UTC", "HH:mm") 
                : flight.fullTakeBackLocalBase && formatInTimeZone(flight.fullTakeBackLocalBase, "UTC", "HH:mm");

              return (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  gap={1} 
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      width: 36,
                      mr: 1,
                    }}
                  >
                    {flight.flightNo}
                  </Typography>
                                    
                  <Typography
                    variant="body2"
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ width: 110 }}
                  >
                    {pickup && `${flight.pickupDep} - ${flight.pickupArr}`}
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      width: 36,
                    }}
                  >
                    {pickup}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      width: 46,
                    }}
                  >
                    {fullAtd}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      width: 36,
                    }}
                  >
                    {atd}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      width: 30,
                    }}
                  >
                    {flight.dep}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      width: 30,
                    }}
                  >
                    {flight.arr}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      width: 36,
                    }}
                  >
                    {ata}
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'left',
                      width: 8,
                    }}
                  >
                    {flight.pax}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      width: 36,
                    }}
                  >
                    {takeBack}
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ width: 110 }}
                  >
                    {takeBack && `${flight.takeBackDep} - ${flight.takeBackArr}`}
                  </Typography>
                </Box>
              )}
            )}
          </Box>
        );
      },
    },  
    {
      field: 'crewMembers',
      headerName: 'CrewMembers',
      flex: 6, 
      renderCell: (params) => {
        const sortedCrewMembers = [...params.row.crewMembers].sort((a, b) => a.rankPriority - b.rankPriority);
  
        return (
          <Box
            sx={{
              height: '100%',
              alignItems: 'flex-start',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {sortedCrewMembers?.map((member, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                alignContent='flex-start'
                gap={1}
                sx={{
                  color: member?.gender === 'FM' 
                    ? theme.palette.secondary.main 
                    : theme.palette.primary.dark,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    width: '30px',
                  }}
                >
                  {member.crewCode}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'left',
                  }}
                >
                  {member.crewName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                  }}
                >
                  ({member.post} | {member.base}{member.trnDuty && ` | ${member.trnDuty}`})
                </Typography>                
              </Box>
            ))}
          </Box>
        );
      },
    },    
    {
      field: "crewCount",
      headerName: "Count",
      disableColumnMenu: true,
      align: 'center',
      flex: 1,
    },    
  ], [
    timeMode, 
    theme.palette,
  ]).filter(Boolean);

  const modeConfig = CYCLE_MODES.find(m => m.value === shuttleMode);

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title={"Crew Shuttles"}
          subtitle={`${loading ? 'Loading...' : 'List of Shuttles'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={modeConfig.label} arrow>
              <StyledButton
                onClick={() => setShuttleMode(
                  CYCLE_MODES[(CYCLE_MODES.indexOf(modeConfig) + 1) % CYCLE_MODES.length].value
                )}
                sx={modeConfig.sx}
              >
                {modeConfig.icon}
              </StyledButton>
            </Tooltip>
            
            <ToggleButtonGroup
              size="small"
              value={view}
              exclusive
              onChange={(e, value) => value && setView(value)}
            >
              {[
                { value: "list", icon: <ViewQuilt /> },
                { value: "table", icon: <ViewModule /> },
              ].map(({ value, icon }) => (
                <ToggleButton aria-label={value} key={value} value={value}>
                  {icon}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControl
              size="small"   
              sx={{ minWidth: 75 }}
            >
              <InputLabel>Port</InputLabel>
              <Select
                label="Port"
                name="port"
                value={port}
                onChange={(e) => setPort(e.target.value)}
              >
                <MenuItem key='none' value=''>All</MenuItem>
                {ports.map((portCode, index) => (                                    
                  <MenuItem key={index} value={portCode}>
                    {portCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}          
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box 
          ref={scrollRef} 
          height={LAYOUT_HEIGHT} 
          sx={{ overflowY: 'auto' }}
        >
          {view === "list" ? (
            <ShuttleList
              crewShuttles={filteredCrewShuttles}
              port={port}
              shuttleMode={shuttleMode}
            />
          ) : (
            <ShuttleTable
              crewShuttles={filteredCrewShuttles}
              columns={columns}
              loading={loading}
            />
          )}

          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      )}
    </Box>
  );
};

export default CrewShuttles;
